import React, { useState } from 'react';
import classnames from 'classnames';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import Styled from 'styled';
import { useMutation } from '@apollo/client/react/hooks';

import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import Avatar from 'four-nets/avatar/avatar';
import { displayedName } from 'four-nets/user/utils';
import { Buyer } from 'four-nets/bazaar/bazaar';

import buyerSellerRemoveMutation, {
    BuyerSellerRemoveMutation,
    BuyerSellerRemoveMutationVariables,
} from './mutations/buyer-seller-remove.graphql';
import buyerSellerRestoreMutation, {
    BuyerSellerRestoreMutation,
    BuyerSellerRestoreMutationVariables,
} from './mutations/buyer-seller-restore.graphql';
import defaultStyles from './buyer-view.scss';
import { toGlobalId } from 'graphql-relay';

export interface Props {
    styles: typeof defaultStyles;
    index: number;
    column?: number | boolean;
    buyer: Buyer;
    messagesCount: number;
    newMessagesCount: number;
    createdDate: Date;
    current: boolean;
    showCanceled: boolean;
}

const BuyerView: React.FC<Props> = props => {
    const { styles, index, buyer, current, messagesCount, newMessagesCount, createdDate, column, showCanceled } = props;
    const buyerId = toGlobalId('BuyerNode', `${buyer.id}`);

    const [manualState, setManualState] = useState<null | boolean>(null);

    const history = useHistory();
    const location = useLocation();

    const [buyerSellerRemove] = useMutation<BuyerSellerRemoveMutation, BuyerSellerRemoveMutationVariables>(
        buyerSellerRemoveMutation
    );
    const [buyerSellerRestore] = useMutation<BuyerSellerRestoreMutation, BuyerSellerRestoreMutationVariables>(
        buyerSellerRestoreMutation
    );

    const onClick = () => {
        history.push({
            pathname: location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(location.search),
                username: props.buyer.username,
            })}`,
        });
    };

    const canceled = (buyer.isCanceled && manualState !== true) || manualState === false;

    if (canceled && !showCanceled) {
        return null;
    }

    return (
        <div className={classnames(styles.inlineWrapper, { [styles.canceled]: canceled })}>
            <button
                className={classnames(styles.buyer, {
                    [styles.new]: newMessagesCount > 0,
                    [styles.noMarginRight]: column || (index + 1) % 4 === 0,
                    [styles.column]: column,
                    [styles.current]: current,
                    [styles.canceled]: canceled,
                })}
                onClick={onClick}
            >
                <Avatar style={{ avatar: styles.avatar }} user={buyer.user} />
                <div className={styles.rows}>
                    <div className={styles.row}>
                        <span className={styles.username}>
                            {buyer.user ? displayedName(buyer.user) : buyer.username}
                        </span>
                        {column ? (
                            <span className={styles.time}>
                                <FormattedDateAgoView value={createdDate} />
                            </span>
                        ) : null}
                    </div>
                    <div className={styles.row}>
                        <span className={styles.messagesCount}>
                            <FormattedMessage id='%(counter)s_CONTACT_MESSAGES' values={{ counter: messagesCount }} />
                            {newMessagesCount > 0 ? (
                                <span className={styles.highlight}>
                                    <FormattedMessage
                                        id='%(counter)s_NEW_CONTACT_MESSAGES'
                                        values={{ counter: newMessagesCount }}
                                    />
                                </span>
                            ) : null}
                        </span>
                    </div>
                </div>
            </button>
            {column ? (
                <button
                    className={styles.delete}
                    onClick={() => {
                        if (canceled) {
                            buyerSellerRestore({ variables: { input: { buyerId } } }).then(result => {
                                if (
                                    result.data &&
                                    result.data.buyerSellerRestore &&
                                    result.data.buyerSellerRestore.success
                                ) {
                                    setManualState(true);
                                }
                            });
                        } else {
                            buyerSellerRemove({ variables: { input: { buyerId } } }).then(result => {
                                if (
                                    result.data &&
                                    result.data.buyerSellerRemove &&
                                    result.data.buyerSellerRemove.success
                                ) {
                                    setManualState(false);
                                }
                            });
                        }
                    }}
                >
                    {canceled ? (
                        <FormattedMessage id='RESTORE_BUYER' values={{ gender: 'v' }} />
                    ) : (
                        <FormattedMessage id='BUTTON_LABEL_DELETE' />
                    )}
                </button>
            ) : null}
        </div>
    );
};

export default Styled(defaultStyles)(BuyerView);
